import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import dayjs, { Dayjs } from 'dayjs'

import { TranslateService } from '@ngx-translate/core'

import { DataRangeEvent, DatepickerRange } from '@shared/models/datepicker-range.model'
import { AuthService } from '@app/modules/auth/auth.service'
import { dateFormats } from '@core/constants/dateFormats'
import { filter, takeUntil, first, catchError, tap, switchMap, map, distinctUntilChanged } from 'rxjs/operators'

import { DestroyService } from '@core/services/destroy.service'
import { Observable, Subscription, of } from 'rxjs'
import { Timezone } from '@app/shared/models/timezone.model'
import { ApiService } from '@app/shared/services/api.service'
import { DataService } from '@app/shared/services/data.service'
import { start } from 'repl'
import { User } from '@app/shared/models/user.model'

export type RegisterEvent = (fn?: DatepickerRange<Dayjs> | Dayjs) => void
export type DatepickerRangeChange = { startDate: Dayjs; endDate: Dayjs } | Dayjs

type OnChangeType = {
  startDate?: string | Dayjs
  endDate?: string | Dayjs
  isRange?: boolean
  value?: string
  chosenLabel?: string
}

@Component({
  selector: 'lin-datepicker-field',
  templateUrl: './datepicker-field.component.html',
  styleUrls: ['./datepicker-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerFieldComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DatepickerFieldComponent implements ControlValueAccessor, OnInit {
  control = new FormControl()

  locale: { applyLabel: string; format: string }
  maxDate: string
  ranges: { [k: string]: Dayjs[] }
  isDataPickerOpen = false

  timezones: Timezone[] = []
  filteredTimezones: Timezone[] = []
  userTimezone: Timezone | undefined
  user: User

  @Input() isNoMaxDate: boolean
  @Input() isRange: boolean
  @Input() isPlaceholderShown: boolean
  @Input() isWithTimePicker: boolean
  @Input() noAutoApply: boolean
  @Input() placeholder: string
  @Input() minDate: string
  @Input() isDisabled: boolean
  @Input() isError: boolean
  @Output() datePickerRangeChange = new EventEmitter<DatepickerRangeChange>()

  private startDateDefault = dayjs().tz().startOf('day')
  private endDateDefault = dayjs().tz().endOf('day')

  @ViewChild('dateInput') dateInput: ElementRef

  @HostListener('window:click', ['$event.target'])
  onClick(targetElement: HTMLElement): void {
    const elementId = 'cdkOverlayTrigger'
    if (targetElement.id !== elementId && this.isDataPickerOpen) {
      this.isDataPickerOpen = false
    }
  }

  @HostListener('window:keypress', ['$event'])
  onEnter(event: KeyboardEvent): void {
    if (event.code === 'Enter') {
      ;(<HTMLElement>event.target).id === 'cdkOverlayTrigger' && void event.preventDefault() && this.handleInputChange(event)
      this.isDataPickerOpen = false
    }
  }

  get dateFormat(): string {
    const dateFormat = this.authService.getCurrentUserDateFormat() || dateFormats.date
    return this.isWithTimePicker ? `${dateFormat} HH:mm` : dateFormat
  }

  constructor(
    private translateService: TranslateService,
    private readonly authService: AuthService,
    private readonly destroyService: DestroyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dataService: DataService,
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$
      .pipe(
        tap((user: User) => {
          this.user = user
        }),
        switchMap(() => this.loadTimezones()),
        tap(() => {
          this.setUserTimezone()
          this.storeUserTimezone()
          this.initSettings()
        }),
        switchMap(() => this.control.valueChanges.pipe(takeUntil(this.destroyService))),
      )
      .subscribe((value: DataRangeEvent) => {
        if (value.chosenLabel && this.dateInput) {
          ;(<HTMLInputElement>this.dateInput.nativeElement).value = value.chosenLabel
        }
      })
  }

  openDatepicker(): void {
    this.maxDate = !this.isNoMaxDate && dayjs().tz().endOf('day').format('YYYY/MM/DD HH:mm')
    this.startDateDefault = dayjs().tz().startOf('day')
    this.endDateDefault = dayjs().tz().endOf('day')

    this.isDataPickerOpen = true
    this.initRanges()

    this.cdr.detectChanges()
  }

  get startDate(): Dayjs {
    return (this.control?.value as DataRangeEvent)?.startDate || this.startDateDefault
  }

  get endDate(): Dayjs {
    return (this.control?.value as DataRangeEvent)?.endDate || this.endDateDefault
  }

  handleInputChange(event: Event): void {
    const valueString = (event.target as HTMLInputElement).value

    const value = this.transformStringToDate(valueString)
    const startDate = this.isValueOrNull(value, 'startDate')
    const endDate = this.isValueOrNull(value, 'endDate')
    const chosenLabel = value ? this.getChosenLabel({ startDate, endDate }) : ''

    this.chooseDate({ startDate, endDate, chosenLabel })
  }

  chooseDate(event: DataRangeEvent): void {
    // const startDate = this.isValueOrNull(event, 'startDate')?.startOf('day');
    // const endDate = this.isValueOrNull(event, 'endDate')?.endOf('day');
    //const datePickerValue: DatepickerRangeChange = this.isRange ? { startDate, endDate } : startDate;
    //const value = { ...event, startDate, endDate };

    const startDateRaw = event.startDate != null ? dayjs((event.startDate as Dayjs).valueOf()) : null
    const endDateRaw = event.endDate != null ? dayjs((event.endDate as Dayjs).valueOf()) : null

    const startDate = this.isWithTimePicker ? startDateRaw : startDateRaw?.startOf('day')
    const endDate = this.isWithTimePicker ? endDateRaw : endDateRaw?.endOf('day')

    const datePickerValue: DatepickerRangeChange = this.isRange ? { startDate, endDate } : startDate
    const value = { ...event, startDate, endDate }

    this.control.setValue(value)
    this.datePickerRangeChange.emit(datePickerValue)
    this.isDataPickerOpen = !this.isDataPickerOpen
  }

  private initRanges(): void {
    if (this.ranges) return

    const currentDate = dayjs().tz()
    this.ranges = this.isRange
      ? {
          [this.translateService.instant('DATE_RANGES.TODAY')]: [currentDate.startOf('day'), currentDate.endOf('day')],
          [this.translateService.instant('DATE_RANGES.YESTERDAY')]: [
            currentDate.subtract(1, 'days').startOf('day'),
            currentDate.subtract(1, 'days').endOf('day'),
          ],
          [this.translateService.instant('DATE_RANGES.LAST_7_DAYS')]: [
            currentDate.subtract(6, 'days').startOf('day'),
            currentDate.endOf('day'),
          ],
          [this.translateService.instant('DATE_RANGES.LAST_30_DAYS')]: [
            currentDate.subtract(29, 'days').startOf('day'),
            currentDate.endOf('day'),
          ],
          [this.translateService.instant('DATE_RANGES.THIS_MONTH')]: [currentDate.startOf('month'), currentDate.endOf('month')],
          [this.translateService.instant('DATE_RANGES.LAST_MONTH')]: [
            currentDate.subtract(1, 'month').startOf('month'),
            currentDate.subtract(1, 'month').endOf('month'),
          ],
        }
      : {}
  }

  handleFocus(): void {
    this.maxDate = !this.isNoMaxDate && dayjs().tz().endOf('day').format('YYYY/MM/DD HH:mm')
    this.initRanges()
    this.cdr.detectChanges()
  }

  writeValue(obj: Pick<DataRangeEvent, 'endDate' | 'startDate'> | Dayjs): void {
    const valueDate = obj
    const startDate = valueDate && 'startDate' in valueDate ? dayjs(valueDate.startDate) : valueDate
    const endDate = valueDate && 'endDate' in valueDate ? dayjs(valueDate.endDate) : valueDate
    const data = { startDate, endDate }

    // emitEvent: false — writeValue is a programmatic write from parent FormControl.
    // Without this, the internal control's valueChanges would fire and trigger registerOnChange's
    // fn() callback, which marks the parent FormControl as dirty (breaking pristine reset after save).
    this.control?.setValue({ ...data, chosenLabel: this.getChosenLabel(obj) }, { emitEvent: false })
  }

  registerOnChange(fn: RegisterEvent): void {
    this.control?.valueChanges
      .pipe(
        takeUntil(this.destroyService),
        distinctUntilChanged((prev, curr) => dayjs(prev?.startDate).isSame(curr?.startDate) && dayjs(prev?.endDate).isSame(curr?.endDate)),
      )
      .subscribe((rangePickerValue: DatepickerRange<Dayjs>) => {
        const startDate = this.isValueOrNull(rangePickerValue, 'startDate')
        const endDate = this.isValueOrNull(rangePickerValue, 'endDate')
        const chosenLabel = this.getChosenLabel(rangePickerValue)

        const data = { startDate, endDate, chosenLabel }
        const datePickerValue = this.isRange ? data : data?.startDate

        fn(datePickerValue)
      })
  }

  registerOnTouched(fn: RegisterEvent): void {
    const valueChangeSubscribe = (value: DatepickerRange<Dayjs>): void => {
      fn(value)
    }

    this.control?.valueChanges.pipe(filter(Boolean), takeUntil(this.destroyService)).subscribe(valueChangeSubscribe)
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable({ emitEvent: false }) : this.control.enable()
  }

  private transformStringToDate = (value: string): OnChangeType => {
    const isRange = value.split(' - ').length >= 2
    const validDateOrDefault = this.dateIsValid(value)
      ? dayjs(value, this.dateFormat).format(this.dateFormat)
      : this.startDateDefault.format(this.dateFormat)

    if (isRange) {
      return {
        startDate: value.split(' - ')[0],
        endDate: value.split(' - ')[1],
        isRange,
      }
    }
    return {
      startDate: validDateOrDefault,
      endDate: validDateOrDefault,
      isRange,
    }
  }

  private initSettings(): void {
    const applyLabel = this.translateService.instant('MAIN.DONE') as string
    const displayFormat = this.dateFormat
    this.maxDate = !this.isNoMaxDate && dayjs().tz().endOf('day').format('YYYY/MM/DD HH:mm')
    this.locale = { applyLabel: applyLabel, format: displayFormat }
    this.placeholder = this.isPlaceholderShown && ((this.placeholder || this.translateService.instant('MAIN.CHOOSE_DATE')) as string)

    this.initRanges()
  }
  private getChosenLabel(obj: Pick<DataRangeEvent, 'endDate' | 'startDate'> | Dayjs): string {
    if (!obj) {
      return this.placeholder && this.isPlaceholderShown ? (this.translateService.instant('MAIN.CHOOSE_DATE') as string) : ''
    }

    if ('startDate' in obj && 'endDate' in obj) {
      const startDate = this.isValidOrNow(obj.startDate).format(this.dateFormat)
      const endDate = this.isValidOrNow(obj.endDate).format(this.dateFormat)
      return this.isRange ? `${startDate} - ${endDate}` : startDate
    }

    return this.isValidOrNow(obj).format(this.dateFormat)
  }

  private isValidOrNow(value: Dayjs | string | undefined): Dayjs {
    const date = typeof value === 'string' ? dayjs(value, this.dateFormat) : dayjs(value)
    return date.isValid() ? date : dayjs()
  }

  //ensure endDate is set correctly when range is selected
  //Added null check before applying .endOf('day') to prevent endDate from being null.
  isValueOrNull(event: any, key: keyof DataRangeEvent): any {
    return event?.[key] ?? null
  }

  // issue is too complex and fragile, if label format or time is off, it silently fails and makes the endDate = null
  //   private isValueOrNull = (value: OnChangeType, date: 'startDate' | 'endDate'): Dayjs | null => {

  //     const storedTimezone = this.user.timezone.name || 'UTC';

  //     const chosenLabel = value.chosenLabel || '';
  //     const dateStrings = chosenLabel.split(' - ');

  //     let parsedStartDate: Dayjs;
  //     let parsedEndDate: Dayjs;

  //     const ensureTime = (dateString: string): string => {
  //         return dateString.match(/\d{2}:\d{2}/) ? dateString : `${dateString} 00:00`;
  //     };

  //     if (dateStrings.length === 1) {
  //         parsedStartDate = dayjs.tz(ensureTime(dateStrings[0]), this.dateFormat, storedTimezone);
  //         parsedEndDate = parsedStartDate;
  //     } else {
  //         parsedStartDate = dayjs.tz(ensureTime(dateStrings[0]), this.dateFormat, storedTimezone);
  //         parsedEndDate = dayjs.tz(ensureTime(dateStrings[1]), this.dateFormat, storedTimezone);
  //     }

  //     let parsedDate: Dayjs;
  //     if (date === 'startDate') {
  //         parsedDate = parsedStartDate;
  //     } else if (date === 'endDate') {
  //         parsedDate = parsedEndDate;
  //     } else {
  //         return null;
  //     }

  //     // Adjusting to include time correctly
  //     if (date === 'endDate' && parsedEndDate.isAfter(parsedStartDate, 'day')) {
  //         return this.dateIsValid(parsedEndDate)
  //             ? parsedEndDate.endOf('day')
  //             : null;
  //     }

  //     return this.dateIsValid(parsedDate)
  //         ? parsedDate
  //         : null;
  // }

  private dateIsValid(date: string | Dayjs): boolean {
    date = dayjs(date, this.dateFormat)
    return dayjs(date).isValid() && (this.isNoMaxDate || dayjs(date).tz().isSameOrBefore(this.maxDate))
  }

  private loadTimezones(): Observable<void> {
    return this.dataService.getTimezones().pipe(
      tap((timezones: Timezone[]) => {
        this.timezones = timezones
        this.filteredTimezones = timezones
      }),
      map(() => void 0),
      catchError((error) => {
        return of(void 0)
      }),
    )
  }

  private setUserTimezone(): void {
    if (this.user && this.user.timezone && this.user.timezone.id) {
      const userTimezoneName = this.user.timezone.name
      this.userTimezone = this.filteredTimezones.find((timezone) => timezone.name === userTimezoneName)
    }
  }

  private storeUserTimezone(): void {
    if (this.userTimezone) {
      localStorage.setItem('storedTimezone', JSON.stringify(this.userTimezone))
    }
  }
}
